import { createRouter, createWebHistory } from "vue-router";
import { redirectLegacyHash } from "@/lib/legacyHash.js";

const routes = [
  {
    meta: {
      title: "Dashboard",
      requiresAuth: true,
    },
    path: "/",
    name: "dashboard",
    component: () => import("@/views/HomeView.vue"),
  },
  {
    meta: {
      title: "Reviews",
      requiresAuth: true,
    },
    path: "/reviews",
    name: "reviews",
    component: () => import("@/views/ReviewsView.vue"),
  },
  {
    // Priority Inbox was merged into Reviews; land on the saved needs-response view.
    path: "/priority-inbox",
    redirect: { path: "/reviews", query: { view: "needs-response" } },
  },
  {
    meta: { requiresAuth: true, requiresBranches: true, title: "Branches" },
    path: "/branches",
    name: "branches",
    component: () => import("@/views/BranchComparisonView.vue"),
  },
  {
    meta: {
      title: "Social Inbox",
      requiresAuth: true,
      requiresSuperuser: true,
    },
    path: "/social-inbox",
    name: "social-inbox",
    component: () => import("@/views/SocialInboxView.vue"),
  },
  {
    meta: {
      title: "Topics",
      requiresAuth: true,
    },
    path: "/topics",
    name: "topics",
    component: () => import("@/views/TopicsView.vue"),
  },
  {
    meta: {
      title: "Entities",
      requiresAuth: true,
    },
    path: "/entities",
    name: "entities",
    component: () => import("@/views/EntitiesView.vue"),
  },
  {
    meta: {
      title: "Social Topics",
      requiresAuth: true,
      requiresSuperuser: true,
    },
    path: "/social-topics",
    name: "social-topics",
    component: () => import("@/views/SocialTopicsView.vue"),
  },
  {
    meta: {
      title: "Social Entities",
      requiresAuth: true,
      requiresSuperuser: true,
    },
    path: "/social-entities",
    name: "social-entities",
    component: () => import("@/views/SocialEntitiesView.vue"),
  },
  {
    meta: {
      title: "Competitors",
      requiresAuth: true,
    },
    path: "/competitors",
    name: "competitors",
    component: () => import("@/views/CompetitorView.vue"),
  },
  {
    meta: {
      title: "Social Buzz",
      requiresAuth: true,
    },
    path: "/socials",
    name: "socials",
    component: () => import("@/views/SocialBuzzView.vue"),
  },
  {
    // "Configuration" is a sidebar group header, not a page. The bare path was
    // an orphaned route that rendered a barren Facebook-only screen (reachable
    // only by typing the URL) and bled the dashboard subtitle. Redirect it to
    // the first real settings page instead of landing anyone on that stub.
    path: "/configuration",
    redirect: "/configuration/channels",
  },
  {
    meta: {
      title: "Channels",
      requiresAuth: true,
    },
    path: "/configuration/channels",
    name: "channels",
    component: () => import("@/views/ChannelManagementView.vue"),
  },
  {
    meta: { requiresAuth: true, title: "Locations" },
    path: "/configuration/locations",
    name: "locations",
    component: () => import("@/views/LocationManagementView.vue"),
  },
  {
    meta: {
      title: "Forms",
    },
    path: "/forms",
    name: "forms",
    component: () => import("@/views/FormsView.vue"),
  },
  {
    meta: {
      title: "Profile",
      requiresAuth: true,
    },
    path: "/profile",
    name: "profile",
    component: () => import("@/views/ProfileView.vue"),
  },
  {
    meta: {
      title: "Ui",
    },
    path: "/ui",
    name: "ui",
    component: () => import("@/views/UiView.vue"),
  },
  {
    meta: {
      title: "Login",
    },
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginView.vue"),
  },
  {
    meta: {
      title: "Authenticating...",
    },
    path: "/auth/callback",
    name: "auth-callback",
    component: () => import("@/views/AuthCallbackView.vue"),
  },
  {
    meta: {
      title: "Forgot password",
    },
    path: "/forgot-password",
    name: "forgot-password",
    component: () => import("@/views/ForgotPasswordView.vue"),
  },
  {
    meta: {
      title: "Reset password",
    },
    path: "/reset-password/:uid/:token",
    name: "reset-password",
    component: () => import("@/views/ResetPasswordView.vue"),
  },
  {
    meta: {
      title: "Awaiting Account Activation",
    },
    path: "/awaiting-activation",
    name: "awaiting-activation",
    component: () => import("@/views/AwaitingActivation.vue"),
  },
  {
    meta: {
      title: "Sign Up",
    },
    path: "/signup",
    name: "signup",
    component: () => import("@/views/SignupView.vue"),
  },
  {
    meta: {
      title: "Get started",
    },
    path: "/onboarding",
    name: "onboarding",
    component: () => import("@/views/OnboardingView.vue"),
  },
  {
    meta: {
      title: "Terms of Service",
    },
    path: "/terms",
    name: "terms",
    component: () => import("@/views/TermsView.vue"),
  },
  {
    meta: {
      title: "Privacy Policy",
    },
    path: "/privacy",
    name: "privacy",
    component: () => import("@/views/PrivacyView.vue"),
  },
  {
    meta: {
      title: "Try a live demo",
    },
    path: "/demo",
    name: "demo",
    component: () => import("@/views/DemoOnboardingView.vue"),
  },
  {
    meta: {
      title: "Error",
    },
    path: "/error",
    name: "error",
    component: () => import("@/views/ErrorView.vue"),
  },
  {
    meta: {
      title: "Analytics",
      requiresAuth: true,
    },
    path: "/analytics",
    name: "analytics",
    component: () => import("@/views/AnalyticsView.vue"),
  },
  {
    meta: {
      title: "Team",
      requiresAuth: true,
    },
    path: "/team",
    name: "team",
    component: () => import("@/views/TeamView.vue"),
  },
  {
    meta: {
      title: "Insights",
      requiresAuth: true,
    },
    path: "/insights",
    name: "insights",
    component: () => import("@/views/InsightsView.vue"),
  },
  {
    meta: {
      title: "Accept Invitation",
    },
    path: "/invite/:token",
    name: "invite-accept",
    component: () => import("@/views/InviteAcceptView.vue"),
  },
  {
    path: "/dashboard-overview",
    redirect: "/analytics",
  },
  {
    meta: {
      title: "Select Organisation",
      requiresAuth: true,
    },
    path: "/organisation/select",
    name: "organisation-select",
    component: () => import("@/views/OrganisationSelectView.vue"),
  },
  {
    meta: { title: "Admin", requiresAuth: true, requiresSuperuser: true },
    path: "/backend/admin",
    name: "admin-home",
    component: () => import("@/views/admin/AdminOverview.vue"),
  },
  {
    meta: { title: "Scrape Jobs", requiresAuth: true, requiresSuperuser: true },
    path: "/backend/admin/scrape-jobs",
    name: "admin-scrape-jobs",
    component: () => import("@/views/admin/ScrapeJobsView.vue"),
  },
  {
    meta: {
      title: "Onboard Customer",
      requiresAuth: true,
      requiresSuperuser: true,
    },
    path: "/backend/admin/onboard-customer",
    name: "admin-onboard-customer",
    component: () => import("@/views/admin/OnboardCustomerView.vue"),
  },
  {
    meta: {
      title: "Rediscover Sources",
      requiresAuth: true,
      requiresSuperuser: true,
    },
    path: "/backend/admin/rediscover-sources",
    name: "admin-rediscover-sources",
    component: () => import("@/views/admin/RediscoverSourcesView.vue"),
  },
  {
    meta: { title: "LLM Costs", requiresAuth: true, requiresSuperuser: true },
    path: "/backend/admin/llm-costs",
    name: "admin-llm-costs",
    component: () => import("@/views/admin/LlmCostsView.vue"),
  },
  {
    meta: {
      title: "Outreach Queue",
      requiresAuth: true,
      requiresSuperuser: true,
    },
    path: "/backend/admin/outreach/queue",
    name: "admin-outreach-queue",
    component: () => import("@/views/admin/OutreachQueueView.vue"),
  },
  {
    meta: {
      title: "Outreach Prospects",
      requiresAuth: true,
      requiresSuperuser: true,
    },
    path: "/backend/admin/outreach/prospects",
    name: "admin-outreach-prospects",
    component: () => import("@/views/admin/OutreachProspectsView.vue"),
  },
  {
    meta: {
      title: "Outreach Analytics",
      requiresAuth: true,
      requiresSuperuser: true,
    },
    path: "/backend/admin/outreach/analytics",
    name: "admin-outreach-analytics",
    component: () => import("@/views/admin/OutreachAnalyticsView.vue"),
  },
  // Legacy /admin/* paths redirect to the new admin shell so old links keep working.
  { path: "/admin", redirect: "/backend/admin" },
  { path: "/admin/scrape-jobs", redirect: "/backend/admin/scrape-jobs" },
  {
    path: "/admin/onboard-customer",
    redirect: "/backend/admin/onboard-customer",
  },
  {
    path: "/admin/rediscover-sources",
    redirect: "/backend/admin/rediscover-sources",
  },
  { path: "/admin/llm-costs", redirect: "/backend/admin/llm-costs" },
  {
    path: "/admin/outreach/queue",
    redirect: "/backend/admin/outreach/queue",
  },
  {
    path: "/admin/outreach/prospects",
    redirect: "/backend/admin/outreach/prospects",
  },
  {
    path: "/admin/outreach/analytics",
    redirect: "/backend/admin/outreach/analytics",
  },
];

// Old /#/demo-style links (marketing site, emails) must reach the real page.
// Has to run before createWebHistory() reads the current URL.
redirectLegacyHash();

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

export default router;
